'use client'

import { ReactNode, useEffect, useState } from 'react'

import { useIsRestoring } from '@tanstack/react-query'

import { useBootstrap, type BootstrapPayload } from '@/shared/lib/bootstrap'

import { BootstrapErrorFallback } from './BootstrapErrorFallback'
import { BootstrapLoadingOverlay } from './BootstrapLoadingOverlay'

const MIN_LOADING_MS = 2000

interface BootstrapGateProps {
  children: ReactNode
  initialBootstrap?: BootstrapPayload | null
  loader?: ReactNode
  errorComponent?: ReactNode
}

export function BootstrapGate({
  children,
  initialBootstrap,
  loader,
  errorComponent
}: BootstrapGateProps) {
  const [minDelayElapsed, setMinDelayElapsed] = useState(false)
  const isRestoring = useIsRestoring()
  const { data, isLoading, isError, fetchStatus } = useBootstrap(
    initialBootstrap ?? undefined
  )

  useEffect(() => {
    const id = setTimeout(() => setMinDelayElapsed(true), MIN_LOADING_MS)
    return () => clearTimeout(id)
  }, [])

  const overlay = loader ? <>{loader}</> : <BootstrapLoadingOverlay />

  if (isRestoring) {
    return overlay
  }

  if (!minDelayElapsed) {
    return overlay
  }

  const loading = (isLoading || fetchStatus === 'fetching') && !data
  if (loading) {
    return overlay
  }

  if (isError && !data) {
    return errorComponent ?? <BootstrapErrorFallback />
  }

  return <>{children}</>
}
